import type { BloodTest } from '@careflux/mercury';
import { toBloodTest } from './bloodTestCatalog';
import type { LabTest } from './bloodTests.types';

/** Code-keyed catalog relationships: what a test auto-includes and recommends. */
export interface BloodTestRelations {
  tests: BloodTest[];
  byId: Map<string, BloodTest>;
  included: Map<string, string[]>;
  recommended: Map<string, string[]>;
}

const knownOthers = (codes: string[], self: string, known: Map<string, BloodTest>): string[] =>
  [...new Set(codes)].filter((code) => code !== self && known.has(code));

export const buildRelations = (labTests: LabTest[]): BloodTestRelations => {
  const tests = labTests.map(toBloodTest);
  const byId = new Map(tests.map((test) => [test.id, test]));
  const included = new Map<string, string[]>();
  const recommended = new Map<string, string[]>();
  for (const lab of labTests) {
    const inc = knownOthers(lab.includedTestCodes, lab.code, byId);
    const rec = knownOthers(lab.recommendedTestCodes, lab.code, byId);
    if (inc.length) included.set(lab.code, inc);
    if (rec.length) recommended.set(lab.code, rec);
  }
  return { tests, byId, included, recommended };
};

const lookup = (ids: string[], relations: BloodTestRelations): BloodTest[] =>
  ids.map((id) => relations.byId.get(id)).filter((test): test is BloodTest => test !== undefined);

/** Selected ids plus everything they auto-include, in catalog order. */
export const withIncluded = (ids: string[], relations: BloodTestRelations): BloodTest[] => {
  const wanted = new Set(ids.flatMap((id) => [id, ...(relations.included.get(id) ?? [])]));
  return relations.tests.filter((test) => wanted.has(test.id));
};

export const lockedIds = (selected: BloodTest[], relations: BloodTestRelations): Set<string> =>
  new Set(selected.flatMap((test) => relations.included.get(test.id) ?? []));

export const recommendedFor = (selected: BloodTest[], relations: BloodTestRelations): BloodTest[] => {
  const selectedIds = new Set(selected.map((test) => test.id));
  const ids = new Set(selected.flatMap((test) => relations.recommended.get(test.id) ?? []));
  return lookup(
    [...ids].filter((id) => !selectedIds.has(id)),
    relations
  );
};

export const resolveNames = (map: Map<string, string[]>, relations: BloodTestRelations): Map<string, string[]> =>
  new Map([...map].map(([id, codes]) => [id, lookup(codes, relations).map((test) => test.name)]));

/** Toggle a test; selecting adds its included tests, deselecting drops those no other selection still includes. */
export const toggleTest = (selected: BloodTest[], test: BloodTest, relations: BloodTestRelations): BloodTest[] => {
  const isSelected = selected.some((item) => item.id === test.id);
  if (!isSelected) {
    const selectedIds = new Set(selected.map((item) => item.id));
    return [...selected, ...withIncluded([test.id], relations).filter((item) => !selectedIds.has(item.id))];
  }
  const remaining = selected.filter((item) => item.id !== test.id);
  const stillLocked = lockedIds(remaining, relations);
  const children = new Set(relations.included.get(test.id) ?? []);
  return remaining.filter((item) => !children.has(item.id) || stillLocked.has(item.id));
};
